import React, { useState, useEffect, useRef } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { useCart } from '../context/CartContext'

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [mobileSubmenuOpen, setMobileSubmenuOpen] = useState(false)
  const dropdownRef = useRef(null)
  const location = useLocation()

  // Close mobile menu and dropdown on page navigation
  useEffect(() => {
    setMobileMenuOpen(false)
    setDropdownOpen(false)
    setMobileSubmenuOpen(false)
  }, [location.pathname])

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const isMenuSectionActive = [
    '/helstegt-pattegris',
    '/catering',
    '/mad-ud-af-huset',
  ].includes(location.pathname)

  const [scrolled, setScrolled] = useState(false)
  const { openCart, totalItemCount, grandTotal } = useCart()

  useEffect(() => {
    function handleScroll() {
      setScrolled(window.scrollY > 40)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <>
      {/* Top bar with contact info - placed in normal document flow above sticky header, hidden on tablet and mobile */}
      <div className="hidden xl:block bg-[#C25E27] text-white text-xs border-b border-[#A84E1C]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-x-6">
            <a
              href="tel:25752724"
              className="inline-flex items-center gap-2 hover:text-white/80 transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-phone text-white text-xs" aria-hidden="true" />
              <span className="font-semibold tracking-wide">25 75 27 24</span>
            </a>
            <a
              href="mailto:toemrerovergaardbyg@gmail.com"
              className="inline-flex items-center gap-2 hover:text-white/80 transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-envelope text-white text-xs" aria-hidden="true" />
              <span>toemrerovergaardbyg@gmail.com</span>
            </a>
          </div>

          <div className="flex items-center gap-x-4 text-white/95 text-xs">
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
              <i className="fa-solid fa-location-dot text-white text-xs" aria-hidden="true" />
              Simmelbrovej 52, 7260 Sønder Omme
            </span>
            <a
              href="https://www.instagram.com/overgaard_pattegris?vrfl=MXB0NmY0Z3pkeDZ1bA%3D%3D&utm_source=qr"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Følg Overgaard Pattegrise på Instagram"
              className="inline-flex items-center gap-1.5 hover:text-white/80 transition-colors whitespace-nowrap pl-2 border-l border-white/20"
            >
              <i className="fa-brands fa-instagram text-sm" aria-hidden="true" />
              <span className="font-medium">Følg os</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main navigation container - this is sticky top-0 */}
      <header
        className={`sticky top-0 z-50 bg-white border-b border-[#E6DFD5] transition-shadow duration-200 ${
          scrolled ? 'shadow-md' : ''
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center group focus:outline-none transition-transform duration-200 hover:scale-105"
            aria-label="Overgaard Pattegrise Forside"
          >
            <span className="text-xl sm:text-2xl font-bold tracking-tight text-[#241F1C] group-hover:text-black transition-colors">
              Overgaard Pattegrise
            </span>
          </Link>

          {/* Desktop Navigation (Visible ONLY at 1200px and wider, per requirement) */}
          <nav
            className="hidden min-[1200px]:flex items-center gap-1 text-[15px] font-medium"
            aria-label="Hovednavigation"
          >
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                `px-3.5 py-2 transition-colors ${
                  isActive
                    ? 'text-[#241F1C] font-bold border-b-2 border-[#241F1C]'
                    : 'text-[#5C534D] hover:text-[#241F1C]'
                }`
              }
            >
              Forside
            </NavLink>

            {/* Dropdown for Menus & Catering */}
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`px-3.5 py-2 inline-flex items-center gap-1.5 transition-colors cursor-pointer ${
                  isMenuSectionActive || dropdownOpen
                    ? 'text-[#241F1C] font-bold border-b-2 border-[#241F1C]'
                    : 'text-[#5C534D] hover:text-[#241F1C]'
                }`}
                aria-expanded={dropdownOpen}
                aria-haspopup="true"
              >
                <span>Mad &amp; Menuer</span>
                <i
                  className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${
                    dropdownOpen ? 'rotate-180 text-[#241F1C]' : 'text-[#6E635C]'
                  }`}
                  aria-hidden="true"
                />
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-0 w-56 mt-1 bg-white shadow-xl border border-[#E6DFD5] py-2 z-50">
                  <NavLink
                    to="/helstegt-pattegris"
                    className={({ isActive }) =>
                      `block px-4 py-2.5 text-sm transition-colors ${
                        isActive
                          ? 'text-[#241F1C] font-bold bg-[#F3EFEA]'
                          : 'text-[#241F1C] hover:bg-[#FAF8F5]'
                      }`
                    }
                  >
                    Helstegt pattegris
                  </NavLink>

                  <NavLink
                    to="/catering"
                    className={({ isActive }) =>
                      `block px-4 py-2.5 text-sm transition-colors ${
                        isActive
                          ? 'text-[#241F1C] font-bold bg-[#F3EFEA]'
                          : 'text-[#241F1C] hover:bg-[#FAF8F5]'
                      }`
                    }
                  >
                    Catering
                  </NavLink>

                  <NavLink
                    to="/mad-ud-af-huset"
                    className={({ isActive }) =>
                      `block px-4 py-2.5 text-sm transition-colors ${
                        isActive
                          ? 'text-[#241F1C] font-bold bg-[#F3EFEA]'
                          : 'text-[#241F1C] hover:bg-[#FAF8F5]'
                      }`
                    }
                  >
                    Mad ud af huset
                  </NavLink>
                </div>
              )}
            </div>

            <NavLink
              to="/fest-og-udlejning"
              className={({ isActive }) =>
                `px-3.5 py-2 transition-colors ${
                  isActive
                    ? 'text-[#241F1C] font-bold border-b-2 border-[#241F1C]'
                    : 'text-[#5C534D] hover:text-[#241F1C]'
                }`
              }
            >
              Fest &amp; udlejning
            </NavLink>

            <NavLink
              to="/om-os"
              className={({ isActive }) =>
                `px-3.5 py-2 transition-colors ${
                  isActive
                    ? 'text-[#241F1C] font-bold border-b-2 border-[#241F1C]'
                    : 'text-[#5C534D] hover:text-[#241F1C]'
                }`
              }
            >
              Om os
            </NavLink>

            <NavLink
              to="/salgs-og-leveringsbetingelser"
              className={({ isActive }) =>
                `px-3.5 py-2 transition-colors ${
                  isActive
                    ? 'text-[#241F1C] font-bold border-b-2 border-[#241F1C]'
                    : 'text-[#5C534D] hover:text-[#241F1C]'
                }`
              }
            >
              Handelsbetingelser
            </NavLink>

            <NavLink
              to="/kontakt"
              className={({ isActive }) =>
                `px-3.5 py-2 transition-colors ${
                  isActive
                    ? 'text-[#241F1C] font-bold border-b-2 border-[#241F1C]'
                    : 'text-[#5C534D] hover:text-[#241F1C]'
                }`
              }
            >
              Kontakt
            </NavLink>

            {/* Central CTA: Sammensæt menu */}
            <div className="ml-3 flex items-center gap-2">
              <Link
                to="/bestil"
                className="inline-flex items-center gap-2 bg-[#C25E27] hover:bg-[#A84E1C] text-white px-4 py-2.5 rounded-lg font-semibold text-sm shadow-sm transition-all transform active:scale-95 cta-hop"
              >
                <i className="fa-solid fa-utensils text-xs" aria-hidden="true" />
                <span>Sammensæt menu</span>
              </Link>

              {/* Cart Button */}
              <button
                type="button"
                onClick={openCart}
                className="relative inline-flex items-center justify-center w-10.5 h-10.5 bg-[#FAF8F5] hover:bg-[#F3EFEA] border border-[#E6DFD5] text-[#241F1C] rounded-lg transition-colors cursor-pointer"
                aria-label="Åbn festkurv"
                title="Åbn festkurv"
              >
                <i className="fa-solid fa-cart-shopping text-base text-[#C25E27]" aria-hidden="true" />
                {totalItemCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-5 h-5 px-1 bg-[#C25E27] text-white text-[11px] font-bold rounded-full flex items-center justify-center shadow-sm">
                    {totalItemCount}
                  </span>
                )}
              </button>
            </div>
          </nav>

          {/* Mobile / Tablet Burger Trigger (Visible below 1200px, per requirement) */}
          <div className="flex items-center gap-2 min-[1200px]:hidden">
            {/* Mobile Cart Trigger */}
            <button
              type="button"
              onClick={openCart}
              className="relative p-2.5 text-[#241F1C] bg-[#FAF8F5] border border-[#E6DFD5] rounded-lg cursor-pointer"
              aria-label="Åbn festkurv"
              title="Åbn festkurv"
            >
              <i className="fa-solid fa-cart-shopping text-base text-[#C25E27]" aria-hidden="true" />
              {totalItemCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#C25E27] text-white text-[11px] font-bold rounded-full flex items-center justify-center">
                  {totalItemCount}
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-1 text-[#241F1C] transition-colors cursor-pointer bg-transparent hover:bg-transparent active:bg-transparent border-0 outline-none focus:outline-none shadow-none"
              aria-expanded={mobileMenuOpen}
              aria-label="Åbn navigationsmenu"
            >
              <i
                className={`fa-solid ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-2xl`}
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Drawer (Under 1200px) */}
      {mobileMenuOpen && (
        <div className="min-[1200px]:hidden border-t border-[#E6DFD5] bg-white px-4 py-6 shadow-xl animate-in fade-in duration-200">
          <div className="flex flex-col gap-1">
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                `px-3 py-2 text-base transition-colors ${
                  isActive ? 'text-[#241F1C] font-bold' : 'text-[#5C534D]'
                }`
              }
            >
              Forside
            </NavLink>

            {/* Collapsible Submenu: Mad & Menuer (Closed to begin with) */}
            <div className="py-1">
              <button
                type="button"
                onClick={() => setMobileSubmenuOpen(!mobileSubmenuOpen)}
                className="w-full flex items-center justify-between px-3 py-2 text-base text-[#241F1C] font-medium cursor-pointer"
                aria-expanded={mobileSubmenuOpen}
              >
                <span>Mad &amp; Menuer</span>
                <i
                  className={`fa-solid fa-chevron-down text-xs text-[#6E635C] transition-transform duration-200 ${
                    mobileSubmenuOpen ? 'rotate-180 text-[#241F1C]' : ''
                  }`}
                  aria-hidden="true"
                />
              </button>

              {mobileSubmenuOpen && (
                <div className="mt-1 pl-4 flex flex-col gap-1">
                  <NavLink
                    to="/helstegt-pattegris"
                    className={({ isActive }) =>
                      `px-3 py-2 text-sm transition-colors ${
                        isActive ? 'text-[#241F1C] font-bold' : 'text-[#5C534D]'
                      }`
                    }
                  >
                    Helstegt pattegris
                  </NavLink>
                  <NavLink
                    to="/catering"
                    className={({ isActive }) =>
                      `px-3 py-2 text-sm transition-colors ${
                        isActive ? 'text-[#241F1C] font-bold' : 'text-[#5C534D]'
                      }`
                    }
                  >
                    Catering
                  </NavLink>
                  <NavLink
                    to="/mad-ud-af-huset"
                    className={({ isActive }) =>
                      `px-3 py-2 text-sm transition-colors ${
                        isActive ? 'text-[#241F1C] font-bold' : 'text-[#5C534D]'
                      }`
                    }
                  >
                    Mad ud af huset
                  </NavLink>
                </div>
              )}
            </div>

            <NavLink
              to="/fest-og-udlejning"
              className={({ isActive }) =>
                `px-3 py-2 text-base transition-colors ${
                  isActive ? 'text-[#241F1C] font-bold' : 'text-[#5C534D]'
                }`
              }
            >
              Fest &amp; udlejning
            </NavLink>

            <NavLink
              to="/om-os"
              className={({ isActive }) =>
                `px-3 py-2 text-base transition-colors ${
                  isActive ? 'text-[#241F1C] font-bold' : 'text-[#5C534D]'
                }`
              }
            >
              Om os
            </NavLink>

            <NavLink
              to="/salgs-og-leveringsbetingelser"
              className={({ isActive }) =>
                `px-3 py-2 text-base transition-colors ${
                  isActive ? 'text-[#241F1C] font-bold' : 'text-[#5C534D]'
                }`
              }
            >
              Handelsbetingelser
            </NavLink>

            <NavLink
              to="/kontakt"
              className={({ isActive }) =>
                `px-3 py-2 text-base transition-colors ${
                  isActive ? 'text-[#241F1C] font-bold' : 'text-[#5C534D]'
                }`
              }
            >
              Kontakt
            </NavLink>

            <div className="pt-4 mt-2 border-t border-[#E6DFD5] space-y-3">
              <Link
                to="/bestil"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#C25E27] hover:bg-[#A84E1C] text-white px-4 py-2.5 font-bold text-xs sm:text-sm shadow-sm transition-colors cta-hop"
              >
                <i className="fa-solid fa-utensils text-xs" aria-hidden="true" />
                <span>Sammensæt menu</span>
              </Link>

              <div className="flex flex-col gap-2 pt-2 text-sm text-[#241F1C]">
                <a
                  href="tel:25752724"
                  className="inline-flex items-center gap-2 p-2 text-[#241F1C] font-semibold whitespace-nowrap hover:text-[#C25E27]"
                >
                  <i className="fa-solid fa-phone text-xs" aria-hidden="true" />
                  <span>25 75 27 24</span>
                </a>
                <a
                  href="mailto:toemrerovergaardbyg@gmail.com"
                  className="inline-flex items-center gap-2 p-2 text-[#241F1C] font-medium text-sm whitespace-nowrap hover:text-[#C25E27]"
                >
                  <i className="fa-solid fa-envelope text-xs" aria-hidden="true" />
                  <span>Skriv e-mail</span>
                </a>
                <a
                  href="https://www.instagram.com/overgaard_pattegris?vrfl=MXB0NmY0Z3pkeDZ1bA%3D%3D&utm_source=qr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 p-2 text-[#241F1C] font-medium text-sm whitespace-nowrap hover:text-[#C25E27]"
                >
                  <i className="fa-brands fa-instagram text-base text-[#C25E27]" aria-hidden="true" />
                  <span>Følg os på Instagram (@overgaard_pattegris)</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
      </header>
    </>
  )
}
